import { useState, useEffect } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';

import { UnitApi } from '@/api';
import { IUnitStatusOverview, ICustomUnitStatusDefinition } from '@/api/types';
import { ConfirmTypes, useToastStore } from '@/store';

interface EditUnitStatusModalProps {
  unit: IUnitStatusOverview | null;
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void; // Simplified: Parent refetches data
}

const EditUnitStatusModal = ({
  unit,
  isOpen,
  onClose,
  onSaveSuccess,
}: EditUnitStatusModalProps): JSX.Element => {
  const [availableStatuses, setAvailableStatuses] = useState<
    ICustomUnitStatusDefinition[]
  >([]);
  const [selectedStatusId, setSelectedStatusId] = useState<string | null>(null);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { updateToast } = useToastStore(); // Use the store hook

  // Fetch available statuses when the modal opens or the unit changes
  useEffect(() => {
    if (isOpen && unit) {
      const fetchAvailableStatuses = async () => {
        setIsLoadingStatuses(true);
        try {
          const response = await UnitApi.getAvailableCustomStatuses();
          const statuses = response?.data ?? [];
          setAvailableStatuses(statuses);
          setSelectedStatusId(unit.customStatus?._id ?? 'none');
        } catch (err: any) {
          updateToast({
            open: true,
            message: 'Could not load available statuses.',
            type: ConfirmTypes.WARNING,
          });
          setAvailableStatuses([]);
        } finally {
          setIsLoadingStatuses(false);
        }
      };
      fetchAvailableStatuses();
    }
  }, [isOpen, unit, updateToast]);

  const handleStatusChange = (event: SelectChangeEvent<string | null>) => {
    setSelectedStatusId(event.target.value);
  };

  const handleSave = async () => {
    if (!unit?.latestShiftId) {
      updateToast({
        open: true,
        message: 'Cannot update status: Missing unit shift information.',
        type: ConfirmTypes.ERROR,
      });
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        customStatusId: selectedStatusId === 'none' ? null : selectedStatusId,
      };

      await UnitApi.setUnitShiftCustomStatus(unit.latestShiftId, payload);

      updateToast({
        open: true,
        message: `Status updated successfully for ${unit.name}.`,
        type: ConfirmTypes.SUCCESS,
      });
      onSaveSuccess();
      onClose();
    } catch (err: any) {
      updateToast({
        open: true,
        message: err?.message || 'Failed to update status.',
        type: ConfirmTypes.ERROR,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Reset selection when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedStatusId(null);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Edit Status for {unit?.name}</DialogTitle>
      <DialogContent sx={{ pt: '10px !important' }}>
        {isLoadingStatuses ? (
          <Stack alignItems="center" p={3}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack spacing={2}>
            <div>
              <Typography
                variant="caption"
                display="block"
                gutterBottom
                sx={{ mb: 0.5, color: 'text.secondary' }}
              >
                Current Status
              </Typography>
              <TextField
                value={unit?.customStatus?.description ?? 'None'}
                fullWidth
                disabled
                variant="outlined"
                margin="none"
                size="small"
              />
            </div>
            <div>
              <Typography
                variant="caption"
                display="block"
                gutterBottom
                sx={{ mb: 0.5, color: 'text.secondary' }}
              >
                New Status
              </Typography>
              <FormControl fullWidth margin="none">
                <Select
                  value={selectedStatusId ?? 'none'}
                  onChange={handleStatusChange}
                  displayEmpty
                  size="small"
                  MenuProps={{
                    PaperProps: {
                      sx: { textAlign: 'left' },
                    },
                  }}
                  sx={{
                    textAlign: 'left',
                    '& .MuiSelect-select': {
                      textAlign: 'left',
                    },
                  }}
                >
                  <MenuItem
                    sx={{ textAlign: 'left', justifyContent: 'flex-start' }}
                    value="none"
                  >
                    /
                  </MenuItem>
                  {availableStatuses.map((status) => (
                    <MenuItem
                      key={status._id}
                      value={status._id}
                      sx={{ textAlign: 'left', justifyContent: 'flex-start' }}
                    >
                      {status.description} ({status.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isSaving}
          sx={{ color: 'text.primary' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="error"
          disabled={isLoadingStatuses || isSaving}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUnitStatusModal;
